import type { TaskDto } from '@af/shared';

/** A live delegation: `from` handed a task to `to` and the task is not finished yet. */
export interface DelegationLine {
  from: string;
  to: string;
  taskId: string;
  title: string;
  /** `running` lines are drawn solid and animated; waiting ones dashed. */
  state: 'running' | 'waiting' | 'approval';
}

const ACTIVE = new Set(['running', 'waiting_children', 'queued', 'awaiting_approval']);

/**
 * Unfinished tasks that one agent delegated to another (child task whose parent task belongs to a
 * different agent). These are the reporting lines that are active right now, also when the target is
 * not a direct report.
 */
export function activeDelegations(tasks: TaskDto[]): DelegationLine[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const lines: DelegationLine[] = [];
  for (const t of tasks) {
    if (!t.parentTaskId || !ACTIVE.has(t.status)) continue;
    const parent = byId.get(t.parentTaskId);
    if (!parent || parent.assigneeAgentId === t.assigneeAgentId) continue;
    lines.push({
      from: parent.assigneeAgentId,
      to: t.assigneeAgentId,
      taskId: t.id,
      title: t.title,
      state:
        t.status === 'awaiting_approval'
          ? 'approval'
          : t.status === 'running' || t.status === 'waiting_children'
            ? 'running'
            : 'waiting',
    });
  }
  return lines;
}

/** Strongest state per `from→to` pair, for drawing one line per pair. */
export function delegationByPair(lines: DelegationLine[]): Map<string, DelegationLine & { count: number }> {
  const rank = { approval: 3, running: 2, waiting: 1 } as const;
  const out = new Map<string, DelegationLine & { count: number }>();
  for (const l of lines) {
    const key = `${l.from}->${l.to}`;
    const prev = out.get(key);
    if (!prev) out.set(key, { ...l, count: 1 });
    else out.set(key, { ...(rank[l.state] > rank[prev.state] ? l : prev), count: prev.count + 1 });
  }
  return out;
}
